import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../../components/ui/DataTable';
import { orderingQuotationService } from '../services/orderingQuotationService';
import { Eye, Download, Edit, ArrowRight } from 'lucide-react';
import { OrderingControlPanel } from './control_pannel';
import { Modal } from '../../../components/ui';
import { PdfViewer } from './pdfviewer';

import { downloadPdfFromUrl } from '../utils/downloadPdf';
import { useOrderingFilters } from '../hooks/useOrderingFilters';
import { exportToExcel } from '../../../utils/exportUtils';
import { toast } from 'react-hot-toast';
import Pagination from '../../../components/ui/Pagination';

const getLeadKey = (item) => {
  if (item.leadId && typeof item.leadId === 'object' && item.leadId._id) return item.leadId._id.toString();
  if (item.leadId && typeof item.leadId === 'string') return item.leadId;
  if (item.clientId && typeof item.clientId === 'object' && item.clientId._id) return item.clientId._id.toString();
  if (item.clientId && typeof item.clientId === 'string') return item.clientId;
  if (item.contactObj && item.contactObj._id) return item.contactObj._id.toString();
  if (item.contactRef) return item.contactRef;
  if (item.customId && item.customId !== '-') return item.customId;
  return item._id || 'unknown';
};

export const OrderingTableView = ({ activeTab, onEdit, onAddOrder }) => {
  const navigate = useNavigate();
  const [initialData, setInitialData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Use custom hook for filters and pagination
  const {
    search, setSearch,
    filters, setFilters,
    sortBy, setSortBy,
    filteredData,
    paginatedData,
    currentPage,
    setCurrentPage,
    totalPages,
    showPagination
  } = useOrderingFilters(initialData);

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // PDF View state
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedPdfDoc, setSelectedPdfDoc] = useState(null);

  useEffect(() => {
    const fetchQuotations = async () => {
      setLoading(true);
      try {
        const res = await orderingQuotationService.getQuotations();
        if (res.success && res.data) {
          const groupedMap = new Map();
          res.data.forEach(item => {
            const key = getLeadKey(item);
            const isApproved = (item.status || '').trim().toLowerCase() === 'approved';
            if (!groupedMap.has(key)) {
              groupedMap.set(key, {
                ...item,
                _id: key,
                leadId: item.leadId || item.clientId || item.contactObj || { _id: key },
                quotationsCount: 1,
                approvedCount: isApproved ? 1 : 0,
                allQuotations: [item],
                latestStatus: item.status || 'Draft',
                lastUpdated: item.updatedAt || item.createdAt || item.date || '-',
                orderingDate: item.createdAt || item.date || item.updatedAt || '-',
              });
            } else {
              const existing = groupedMap.get(key);
              existing.quotationsCount += 1;
              if (isApproved) {
                existing.approvedCount = (existing.approvedCount || 0) + 1;
              }
              existing.allQuotations.push(item);

              const currentItemDate = new Date(item.updatedAt || item.createdAt || item.date || 0).getTime();
              const existingDate = new Date(existing.lastUpdated || 0).getTime();
              if (currentItemDate >= existingDate) {
                existing.latestStatus = item.status || 'Draft';
                existing.lastUpdated = item.updatedAt || item.createdAt || item.date || '-';
                if (item.contactObj) existing.contactObj = item.contactObj;
                if (item.projectName) existing.projectName = item.projectName;
                if (item.site) existing.site = item.site;
              }

              const currentCreatedDate = new Date(item.createdAt || item.date || item.updatedAt || 0).getTime();
              const existingCreatedDate = new Date(existing.orderingDate || 0).getTime();
              if (currentCreatedDate > 0 && (existingCreatedDate === 0 || currentCreatedDate < existingCreatedDate)) {
                existing.orderingDate = item.createdAt || item.date || item.updatedAt || '-';
              }
            }
          });
          setInitialData(Array.from(groupedMap.values()));
        }
      } catch (error) {
        console.error("Failed to fetch quotations:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchQuotations();
  }, [activeTab]);

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const exportData = filteredData.map(item => ({
        'Verve ID': item.customId || item.verveId || item.contactObj?.customId || '-',
        'Contact Details': item.contactObj ? (item.contactObj.customerName || `${item.contactObj.firstName || ''} ${item.contactObj.lastName || ''}`.trim() || item.projectName || '-') : (item.projectName || '-'),
        'Location': item.contactObj ? (`${item.contactObj.city || ''}, ${item.contactObj.state || ''}`.replace(/^,\s*|,\s*$/g, '') || item.site || '-') : (item.site || '-'),
        'Project Name': item.projectName || '',
        'Quotations Count': item.quotationsCount || 1,
        'Designer': item.referenceDesigner || '',
        'Last Updated': item.lastUpdated || '-',
        'Latest Status': item.latestStatus || 'Draft',
      }));
      await exportToExcel(exportData, { filename: `Orderings_Leads_Export` });
      toast.success("Export started successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Export failed!");
    } finally {
      setIsExporting(false);
    }
  };

  const columns = [
    {
      header: 'Verve ID',
      accessor: (row) => row.customId || row.verveId || row.contactObj?.customId || '-',
      cellClassName: '!font-normal text-brand-blue !whitespace-nowrap !py-1'
    },
    {
      header: 'Contact Details',
      cellClassName: '!font-normal !whitespace-nowrap !py-1',
      accessor: (row) => {
        const c = row.contactObj;
        if (!c) return <span className="font-normal text-slate-800 text-xs">{row.projectName || row.clientName || '-'}</span>;
        const name = c.customerName || `${c.firstName || ''} ${c.lastName || ''}`.trim() || row.projectName || '-';
        const phone = c.mobile || c.phoneNumber || c.phone || '';
        const email = c.email || '';
        return (
          <div className="flex flex-col min-w-0">
            <span className="font-normal text-slate-800 flex items-center gap-2 text-xs">{name}</span>
            <span className="font-normal text-[11px] text-slate-500 flex items-center gap-1 leading-tight">
              {phone || "No Phone"}
              {email && <span className="mx-1 opacity-50">|</span>}
              {email && email.toLowerCase()}
            </span>
          </div>
        );
      }
    },
    {
      header: 'Location',
      accessor: (row) => {
        const c = row.contactObj;
        const locationStr = c ? (`${c.city || ''}, ${c.state || ''}`.replace(/^,\s*|,\s*$/g, '') || c.addressLine1 || row.site || '-') : (row.site || '-');
        return <div className="truncate max-w-[150px] font-normal" title={locationStr}>{locationStr}</div>;
      },
      cellClassName: '!text-xs !font-normal !py-1'
    },
    /* { header: 'Customer Name', accessor: 'projectName', cellClassName: '!font-normal' }, */
    /* { header: 'Quotation No', accessor: 'quotationNo', cellClassName: '!font-normal' }, */
    /* { header: 'Location', accessor: 'site', cellClassName: '!font-normal' }, */
    {
      header: 'Prepared Quote',
      accessor: (row) => (
        <span className="font-semibold text-slate-800 text-xs">
          {row.quotationsCount || 1}
        </span>
      ),
      cellClassName: '!text-center !whitespace-nowrap !py-1'
    },
    {
      header: 'Approved Quote',
      accessor: (row) => (
        <span className="font-semibold text-slate-800 text-xs">
          {row.approvedCount || 0}
        </span>
      ),
      cellClassName: '!text-center !whitespace-nowrap !py-1'
    },
    /* {
      header: 'Latest Status',
      accessor: (row) => (
        <span className={`px-2.5 py-1 border rounded-md text-[11px] font-semibold ${row.latestStatus === 'Approved' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
          row.latestStatus === 'Rejected' ? 'bg-red-50 text-red-700 border-red-200' :
            row.latestStatus === 'Draft' ? 'bg-slate-50 text-slate-600 border-slate-200' :
              'bg-amber-50 text-amber-700 border-amber-200'
          }`}>
          {row.latestStatus || 'Draft'}
        </span>
      )
    }, */
    {
      header: 'Ordering Date',
      accessor: (row) => {
        const d = row.orderingDate || row.createdAt || row.date || row.lastUpdated;
        if (!d || d === '-') return '-';
        try {
          return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
        } catch (e) {
          return d;
        }
      },
      cellClassName: '!font-normal !whitespace-nowrap text-slate-600 text-xs !py-1'
    },
    {
      header: 'Last Updated',
      accessor: (row) => {
        if (!row.lastUpdated || row.lastUpdated === '-') return '-';
        try {
          return new Date(row.lastUpdated).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
        } catch (e) {
          return row.lastUpdated;
        }
      },
      cellClassName: '!font-normal !whitespace-nowrap text-slate-600 text-xs !py-1'
    },
    {
      header: 'Action',
      accessor: (row) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/ordering/${row._id}`, { state: { lead: row } });
          }}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-brand-blue/10 text-brand-blue rounded-md text-xs font-semibold cursor-pointer hover:scale-105 hover:-translate-y-0.5 active:scale-95 active:translate-y-0 transition-all duration-200 shadow-sm hover:shadow"
        >
          <span>Open Workspace</span>
          <ArrowRight size={14} />
        </button>
      ),
      cellClassName: '!py-1 !whitespace-nowrap'
    }
  ];

  return (
    <>
      <div className="flex-1 mt-4 flex flex-col h-full">
        {/* Control panel placed outside table card for clean standalone spacing */}
        <div className="mb-4">
          <OrderingControlPanel
            search={search}
            setSearch={setSearch}
            filters={filters}
            setFilters={setFilters}
            sortBy={sortBy}
            setSortBy={setSortBy}
            showAdvancedFilters={showAdvancedFilters}
            setShowAdvancedFilters={setShowAdvancedFilters}
            onAddClick={onAddOrder}
            onExport={handleExport}
            isExporting={isExporting}
          />
        </div>
        <div className="flex-1 flex flex-col h-full bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="flex-1 overflow-auto">
            {/* onRowClick={(row) => navigate(`/ordering/${row._id}`, { state: { lead: row } })} */}
            <DataTable
              columns={columns}
              data={paginatedData}
              isLoading={loading}
              loadingText="Loading Orders..."
              rowClassName="hover:bg-slate-50/80 transition-colors !h-8"
            />
          </div>
        </div>
        {!loading && showPagination && (
          <div className="mt-6 bg-white rounded-xl border border-slate-200/80 shadow-sm overflow-hidden">
            <Pagination
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>

      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title={selectedPdfDoc?.title || "Quotation Document"}
        className="max-w-6xl w-full"
      >
        <div className="-m-5 sm:-m-6 h-[85vh] flex flex-col rounded-b-lg sm:rounded-b-xl overflow-hidden">
          <PdfViewer
            selectedDocument={selectedPdfDoc}
            onClose={() => setIsViewModalOpen(false)}
          />
        </div>
      </Modal>
    </>
  );
};
